import { create, type StoreApi } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { UseBoundStore } from 'zustand/react';
import { OrderType } from '@/common/constants';
import {
  clampTerminalLayoutSizes,
  DEFAULT_TERMINAL_LAYOUT_SIZES,
  DEFAULT_TERMINAL_PANELS,
  mergeTerminalPanels,
  type TerminalLayoutSizes,
  type TerminalPanelVisibility,
} from '@/common/terminalLayout.ts';
import { CURRENT_APP_VERSION, clearDataMigration } from '@/utils/zustand_helpers';

export type SiteLayout = 'pro' | 'compact' | 'custom';

export type DefaultOrderType = OrderType.market | OrderType.limit;

export enum AccountDashboardTabIndex {
  POSITIONS = 0,
  OPEN_ORDERS = 1,
  TRADE_HISTORY = 2,
  ORDER_HISTORY = 3,
  PNL_HISTORY = 4,
  ACCOUNT_HISTORY = 5,
  // Persisted in localStorage — append only, never reorder.
  FUNDING_HISTORY = 6,
  MARKET_MAKING = 7,
}

const DEFAULT_LANGUAGE = 'en';
const DEFAULT_LAYOUT: SiteLayout = 'pro';
const DEFAULT_USE_TOP_MARGIN = false;
const DEFAULT_REVERSE_CHART = false;
const DEFAULT_CONFIRM_POSITION_CLOSE = true;
const DEFAULT_CONFIRM_POSITION_FLIP = true;
const DEFAULT_CLOSE_POSITION_IS_LIMIT = false;
const DEFAULT_ORDER_TYPE: DefaultOrderType = OrderType.limit;
const DEFAULT_HIDE_SENSITIVE_DATA = false;
const DEFAULT_CUSTOM_SLIPPAGE_PERCENT: number | null = null;
const DEFAULT_MOBILE_ORDER_BOOK_VISIBLE = true;

export const CUSTOM_SLIPPAGE_MIN_PERCENT = 0.01;
export const CUSTOM_SLIPPAGE_MAX_PERCENT = 100;
export const STANDARD_SLIPPAGE_PRESETS = [1, 2, 3, 5] as const;

interface UiState {
  language: string;
  layout: SiteLayout;
  useTopMargin: boolean;
  reverseChartPosition: boolean;
  accountDashboardTab: AccountDashboardTabIndex;
  blockExplorerUrl?: string;
  confirmPositionClose: boolean;
  confirmPositionFlip: boolean;
  defaultClosePositionIsLimit: boolean;
  defaultOrderType: DefaultOrderType;
  hideSensitiveData: boolean;
  customSlippagePercent: number | null;
  terminalLayoutSizes: TerminalLayoutSizes;
  terminalPanels: TerminalPanelVisibility;
  mobileOrderBookVisible: boolean;
  setLanguage: (language: string) => void;
  setLayout: (layout: SiteLayout) => void;
  setUseTopMargin: (useTopMargin: boolean) => void;
  setReverseChartPosition: (reverseChartPosition: boolean) => void;
  setAccountDashboardTab: (tab: AccountDashboardTabIndex) => void;
  setBlockExplorerUrl: (url?: string) => void;
  setConfirmPositionClose: (confirm: boolean) => void;
  setConfirmPositionFlip: (confirm: boolean) => void;
  setDefaultClosePositionIsLimit: (isLimit: boolean) => void;
  setDefaultOrderType: (orderType: DefaultOrderType) => void;
  toggleHideSensitiveData: () => void;
  setCustomSlippagePercent: (percent: number | null) => void;
  setTerminalLayoutSizes: (sizes: Partial<TerminalLayoutSizes>) => void;
  setTerminalPanelVisible: (panel: keyof TerminalPanelVisibility, visible: boolean) => void;
  setMobileOrderBookVisible: (visible: boolean) => void;
  resetLayoutPreferences: () => void;
}

export const useUiStore: UseBoundStore<StoreApi<UiState>> = create<UiState>()(
  persist(
    (set) => ({
      language: DEFAULT_LANGUAGE,
      layout: DEFAULT_LAYOUT,
      useTopMargin: DEFAULT_USE_TOP_MARGIN,
      reverseChartPosition: DEFAULT_REVERSE_CHART,
      accountDashboardTab: AccountDashboardTabIndex.POSITIONS,
      blockExplorerUrl: undefined as string | undefined,
      confirmPositionClose: DEFAULT_CONFIRM_POSITION_CLOSE,
      confirmPositionFlip: DEFAULT_CONFIRM_POSITION_FLIP,
      defaultClosePositionIsLimit: DEFAULT_CLOSE_POSITION_IS_LIMIT,
      defaultOrderType: DEFAULT_ORDER_TYPE,
      hideSensitiveData: DEFAULT_HIDE_SENSITIVE_DATA,
      customSlippagePercent: DEFAULT_CUSTOM_SLIPPAGE_PERCENT,
      terminalLayoutSizes: DEFAULT_TERMINAL_LAYOUT_SIZES,
      terminalPanels: DEFAULT_TERMINAL_PANELS,
      mobileOrderBookVisible: DEFAULT_MOBILE_ORDER_BOOK_VISIBLE,
      setLanguage: (language) => set({ language }),
      setLayout: (layout) => set({ layout }),
      setUseTopMargin: (useTopMargin) => set({ useTopMargin }),
      setReverseChartPosition: (reverseChartPosition) => set({ reverseChartPosition }),
      setAccountDashboardTab: (accountDashboardTab) => set({ accountDashboardTab }),
      setBlockExplorerUrl: (blockExplorerUrl) => set({ blockExplorerUrl }),
      setConfirmPositionClose: (confirmPositionClose) => set({ confirmPositionClose }),
      setConfirmPositionFlip: (confirmPositionFlip) => set({ confirmPositionFlip }),
      setDefaultClosePositionIsLimit: (defaultClosePositionIsLimit) => set({ defaultClosePositionIsLimit }),
      setDefaultOrderType: (defaultOrderType) => set({ defaultOrderType }),
      toggleHideSensitiveData: () => set((state) => ({ hideSensitiveData: !state.hideSensitiveData })),
      setCustomSlippagePercent: (customSlippagePercent) => set({ customSlippagePercent }),
      setTerminalLayoutSizes: (sizes) =>
        set((state) => ({
          terminalLayoutSizes: clampTerminalLayoutSizes({ ...state.terminalLayoutSizes, ...sizes }),
        })),
      setTerminalPanelVisible: (panel, visible) =>
        set((state) => ({
          terminalPanels: { ...state.terminalPanels, [panel]: visible },
        })),
      setMobileOrderBookVisible: (mobileOrderBookVisible) => set({ mobileOrderBookVisible }),
      resetLayoutPreferences: () =>
        set({
          layout: DEFAULT_LAYOUT,
          useTopMargin: DEFAULT_USE_TOP_MARGIN,
          reverseChartPosition: DEFAULT_REVERSE_CHART,
          defaultClosePositionIsLimit: DEFAULT_CLOSE_POSITION_IS_LIMIT,
          terminalLayoutSizes: DEFAULT_TERMINAL_LAYOUT_SIZES,
          terminalPanels: DEFAULT_TERMINAL_PANELS,
          mobileOrderBookVisible: DEFAULT_MOBILE_ORDER_BOOK_VISIBLE,
        }),
    }),
    {
      name: 'dex-ui-preferences',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<UiState>),
        terminalLayoutSizes: clampTerminalLayoutSizes({
          ...DEFAULT_TERMINAL_LAYOUT_SIZES,
          ...(persistedState as Partial<UiState>)?.terminalLayoutSizes,
        }),
        terminalPanels: mergeTerminalPanels((persistedState as Partial<UiState>)?.terminalPanels),
      }),
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        language: state.language,
        layout: state.layout,
        useTopMargin: state.useTopMargin,
        reverseChartPosition: state.reverseChartPosition,
        confirmPositionClose: state.confirmPositionClose,
        confirmPositionFlip: state.confirmPositionFlip,
        defaultClosePositionIsLimit: state.defaultClosePositionIsLimit,
        defaultOrderType: state.defaultOrderType,
        accountDashboardTab: state.accountDashboardTab,
        hideSensitiveData: state.hideSensitiveData,
        customSlippagePercent: state.customSlippagePercent,
        terminalLayoutSizes: state.terminalLayoutSizes,
        terminalPanels: state.terminalPanels,
        mobileOrderBookVisible: state.mobileOrderBookVisible,
      }),
    }
  )
);
